import type {
  DeveloperFormErrors,
  DeveloperFormValues,
} from './developer-form.js';

/** The sections of the settings form, in the order they show. Each folds like Advanced always has. */
export const DEVELOPER_SECTIONS = [
  'basics',
  'work-sources',
  'knowledge',
  'triggers',
  'alerts',
  'advanced',
] as const;

export type DeveloperSectionId = (typeof DEVELOPER_SECTIONS)[number];

/**
 * Which section is open: one section, `none` once the person folded the open one, or `undefined`
 * before anything chose, which opens the default. `none` has to be its own value so the URL can
 * hold it: without it, folding the default section would open it again at once.
 */
export type OpenSection = DeveloperSectionId | 'none';

/** What is saved for a developer: its stored settings and whether the service holds a Jira token. */
export interface SavedSetup {
  values: DeveloperFormValues;
  hasJiraToken?: boolean;
}

/** The section each refusable field sits in, so a refused save can open it. */
const SECTION_OF_FIELD: Partial<
  Record<keyof DeveloperFormValues, DeveloperSectionId>
> = {
  name: 'basics',
  jiraBaseUrl: 'work-sources',
  jiraToken: 'work-sources',
  alias: 'work-sources',
  jiraInnerDomain: 'work-sources',
  jiraSpaces: 'work-sources',
  repos: 'work-sources',
  salesforceOrgs: 'work-sources',
  confluenceBaseUrl: 'knowledge',
  confluenceUsername: 'knowledge',
  confluenceToken: 'knowledge',
  confluenceSpaces: 'knowledge',
  crashlyticsKeyChange: 'knowledge',
  pollIntervalSeconds: 'advanced',
  developmentSlots: 'advanced',
  deployPipelineRepository: 'advanced',
  deployPipelineWorkflow: 'advanced',
};

/** A Jira site counts when a global secret is linked, or when its URL and its own token are saved. */
function jiraConnected(saved: SavedSetup): boolean {
  const { values } = saved;
  return (
    Boolean(values.jiraCredentialId) ||
    (Boolean(values.jiraBaseUrl.trim()) && saved.hasJiraToken === true)
  );
}

/**
 * The sections that need setup before the developer can work, read from the saved developer only, so
 * unsaved edits never change it. Only Work sources can: without a Jira site and a repository the
 * developer has nowhere to find work and nothing to work on. Every other section is either always
 * filled in (Basics) or works on its defaults, so it never asks for anything.
 */
export function sectionsNeedingSetup(
  saved: SavedSetup,
): ReadonlySet<DeveloperSectionId> {
  const ready = jiraConnected(saved) && saved.values.repos.length > 0;
  return new Set<DeveloperSectionId>(ready ? [] : ['work-sources']);
}

/** The section that opens when nothing chose one: the first shown that needs setup, else the first shown. */
export function defaultOpenSection(
  visible: readonly DeveloperSectionId[],
  needingSetup: ReadonlySet<DeveloperSectionId>,
): DeveloperSectionId {
  return (
    visible.find((section) => needingSetup.has(section)) ??
    visible[0] ??
    'basics'
  );
}

/** The first section, in form order, holding a field the save refused. */
export function sectionOfFirstError(
  errors: DeveloperFormErrors,
): DeveloperSectionId | undefined {
  const refused = new Set(
    Object.keys(errors).map(
      (field) => SECTION_OF_FIELD[field as keyof DeveloperFormValues],
    ),
  );
  return DEVELOPER_SECTIONS.find((section) => refused.has(section));
}

/** Reads the open section from a URL value: a section id or `none`, anything else as unset. */
export function parseDeveloperSection(raw: unknown): OpenSection | undefined {
  if (raw === 'none') return 'none';
  return DEVELOPER_SECTIONS.find((section) => section === raw);
}
